import React, { useState, useRef, useEffect } from 'react';
import { Modal, Descriptions, Tag, Button, Space, message, Typography, Divider, Progress, Alert, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle, faPauseCircle, faCopy, faSync, faCheckCircle, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { callAnalyticsAPI } from '../../../services/callAnalytics';

const { Paragraph, Text, Title } = Typography;

const RecordingDetailsModal = ({ visible, recording, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [recordingDetails, setRecordingDetails] = useState(null);
  const [awsExpanded, setAwsExpanded] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const audioRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (visible && recording) {
      loadRecordingDetails();
    }
    
    return () => {
      // Cleanup audio on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [visible, recording]);

  const loadRecordingDetails = async () => {
    setLoading(true);
    try {
      if (!recording?._id) {
        console.error('Recording ID is missing:', recording);
        message.error('Recording ID is missing');
        return;
      }
      
      console.log('Loading recording details for ID:', recording._id);
      const data = await callAnalyticsAPI.getRecording(recording._id);
      console.log('Recording details loaded:', data);
      // The service already extracts the recording object, so use it directly
      setRecordingDetails(data);
    } catch (error) {
      message.error('Failed to load recording details');
      console.error('Load error:', error);
      console.error('Recording:', recording);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      try {
        if (!recordingDetails?.playbackUrl) {
          message.error('Playback URL not available');
          return;
        }

        if (!audioRef.current) {
          audioRef.current = new Audio(recordingDetails.playbackUrl);
          audioRef.current.onended = () => setPlaying(false);
          audioRef.current.onerror = () => {
            message.error('Failed to play audio');
            setPlaying(false);
          };
        }

        audioRef.current.play();
        setPlaying(true);
      } catch (error) {
        message.error('Failed to play recording');
        console.error('Playback error:', error);
      }
    }
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      const result = await callAnalyticsAPI.transcribeRecording(recording._id, true);
      
      if (result.job) {
        message.success('Transcription queued. This may take a few minutes.');
        setIsPolling(true);
        
        // Clear any existing polling interval
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
        
        // Poll for status updates
        pollIntervalRef.current = setInterval(async () => {
          try {
            const statusData = await callAnalyticsAPI.getRecording(recording._id);
            console.log('[Modal Poll] Status:', statusData.transcriptionStatus, 'AWS Status:', statusData.awsTranscriptionStatus);
            
            // Update the modal with latest data
            setRecordingDetails(statusData);
            
            if (statusData.transcriptionStatus === 'completed') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setIsPolling(false);
              message.success('Transcription completed!');
              if (onUpdate) onUpdate();
            } else if (statusData.transcriptionStatus === 'failed') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setIsPolling(false);
              message.error('Transcription failed');
            }
          } catch (error) {
            console.error('[Modal Poll] Error:', error);
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setIsPolling(false);
          }
        }, 10000); // Poll every 10 seconds (matching backend auto-polling)

        // Stop polling after 10 minutes
        setTimeout(() => {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setIsPolling(false);
            console.log('[Modal Poll] Timeout reached, stopping polling');
          }
        }, 600000);
      }
    } catch (error) {
      message.error('Failed to start transcription');
      console.error('Transcription error:', error);
    } finally {
      setTranscribing(false);
    }
  };

  const handleCopyTranscription = () => {
    if (recordingDetails?.transcriptionText) {
      navigator.clipboard.writeText(recordingDetails.transcriptionText);
      message.success('Transcription copied to clipboard');
    }
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      relevant: 'green',
      irrelevant: 'orange',
      spam: 'red',
      unclear: 'default',
      uncategorized: 'default'
    };
    return colorMap[category] || 'default';
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!recording) return null;

  return (
    <Modal
      title={<Space><Title level={4} style={{ margin: 0 }}>Recording Details</Title></Space>}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={800}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" tip="Loading recording details..." />
        </div>
      ) : recordingDetails ? (
        <div>
          {/* Audio Player */}
          <div style={{ 
            padding: '20px', 
            background: '#f5f5f5', 
            borderRadius: '8px', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text strong style={{ fontSize: '16px' }}>{recordingDetails.filename}</Text>
              <Button
                type="primary"
                size="large"
                icon={playing ? <FontAwesomeIcon icon={faPauseCircle} /> : <FontAwesomeIcon icon={faPlayCircle} />}
                onClick={handlePlayPause}
                loading={loading}
              >
                {playing ? 'Pause' : 'Play Recording'}
              </Button>
              <Text type="secondary">Duration: {formatDuration(recordingDetails.duration)}</Text>
            </Space>
          </div>

          {/* Basic Information */}
          <Descriptions title="Call Information" bordered size="small" column={2}>
            <Descriptions.Item label="Date">
              {moment(recordingDetails.date).format('MMM DD, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Time">
              {recordingDetails.time || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Campaign">
              {recordingDetails.campaignName ? (
                <Tag color="blue">{recordingDetails.campaignName}</Tag>
              ) : (
                <Tag>Unknown</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Direction">
              <Tag color={recordingDetails.direction === 'inbound' ? 'green' : 'orange'}>
                {recordingDetails.direction}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Phone Number">
              {recordingDetails.phoneNumber || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Extension">
              {recordingDetails.extension ? (
                <Tag color="green">{recordingDetails.extension}</Tag>
              ) : (
                'N/A'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              <Tag color={getCategoryColor(recordingDetails.category)}>
                {recordingDetails.category}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="File Size">
              {(recordingDetails.fileSize / 1024 / 1024).toFixed(2)} MB
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* AWS Transcription Section */}
          <div style={{ marginTop: '8px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Space wrap>
                <Title level={5} style={{ margin: 0 }}>Transcription</Title>
                {recordingDetails.transcriptionConfidence && recordingDetails.transcriptionStatus === 'completed' && (
                  <Tag color="green">
                    {recordingDetails.transcriptionConfidence}% Confidence
                  </Tag>
                )}
                {recordingDetails.awsSpeakerCount && (
                  <Tag color="blue">
                    {recordingDetails.awsSpeakerCount} Speakers
                  </Tag>
                )}
              </Space>
              <Space wrap>
                {recordingDetails.transcriptionStatus === 'completed' && recordingDetails.transcriptionText && (
                  <Button
                    size="small"
                    icon={<FontAwesomeIcon icon={faCopy} />}
                    onClick={handleCopyTranscription}
                  >
                    Copy
                  </Button>
                )}
                {['pending', 'failed'].includes(recordingDetails.transcriptionStatus) && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<FontAwesomeIcon icon={faSync} />}
                    onClick={handleTranscribe}
                    loading={transcribing}
                  >
                    Transcribe Now
                  </Button>
                )}
                {recordingDetails.awsSpeakerSegments && recordingDetails.awsSpeakerSegments.length > 20 && (
                  <Button
                    size="small"
                    icon={awsExpanded ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faPlus} />}
                    onClick={() => setAwsExpanded(!awsExpanded)}
                  >
                    {awsExpanded ? 'Show Less' : `Show All (${recordingDetails.awsSpeakerSegments.length})`}
                  </Button>
                )}
              </Space>
            </div>

            {recordingDetails.transcriptionStatus === 'completed' && recordingDetails.awsSpeakerSegments && recordingDetails.awsSpeakerSegments.length > 0 ? (
              <div style={{ 
                padding: '20px', 
                background: '#f0f0f0', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                maxHeight: '500px',
                overflowY: 'auto'
              }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {recordingDetails.awsSpeakerSegments.slice(0, awsExpanded ? undefined : 20).map((segment, idx) => {
                    // Convert spk_0 to "Speaker 1", spk_1 to "Speaker 2", etc.
                    const speakerNumber = parseInt(segment.speaker.replace('spk_', '')) + 1;
                    const speakerLabel = `Speaker ${speakerNumber}`;
                    
                    // iPhone blue for Speaker 1, iPhone gray for Speaker 2+
                    const speakerColor = speakerNumber === 1 ? '#007AFF' : '#8E8E93';
                    
                    return (
                      <div key={idx} style={{ 
                        padding: '10px 14px',
                        background: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e8e8e8'
                      }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            <span style={{ 
                              background: speakerColor,
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '4px',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}>
                              {speakerLabel}
                            </span>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {segment.startTime.toFixed(1)}s - {segment.endTime.toFixed(1)}s
                            </Text>
                          </Space>
                          {segment.confidence > 0 && (
                            <Tag color="default" style={{ fontSize: '11px' }}>
                              {(segment.confidence * 100).toFixed(0)}%
                            </Tag>
                          )}
                        </Space>
                        <Paragraph style={{ 
                          marginBottom: 0,
                          marginTop: '8px',
                          fontSize: '14px',
                          color: '#262626',
                          lineHeight: '1.6'
                        }}>
                          {segment.items.join(' ') || '(no text)'}
                        </Paragraph>
                      </div>
                    );
                  })}
                  {!awsExpanded && recordingDetails.awsSpeakerSegments.length > 20 && (
                    <Text type="secondary" style={{ textAlign: 'center', display: 'block', padding: '8px' }}>
                      ... and {recordingDetails.awsSpeakerSegments.length - 20} more segments
                    </Text>
                  )}
                </Space>
              </div>
            ) : recordingDetails.transcriptionStatus === 'processing' || isPolling ? (
              <Alert
                message={isPolling ? "Transcription in progress (polling for updates...)" : "Transcription in progress"}
                description={
                  <Space direction="vertical">
                    <Text>The recording is being transcribed. This may take 30-60 seconds.</Text>
                    {isPolling && <Text type="secondary">Checking for updates every 10 seconds...</Text>}
                  </Space>
                }
                type="info"
                showIcon
                icon={<Spin />}
              />
            ) : recordingDetails.transcriptionStatus === 'failed' ? (
              <Alert
                message="Transcription failed"
                description={recordingDetails.transcriptionError || 'An error occurred during transcription'}
                type="error"
                showIcon
              />
            ) : (
              <Alert
                message="Not yet transcribed"
                description="Click 'Transcribe Now' to generate a transcription of this recording."
                type="warning"
                showIcon
              />
            )}
          </div>

          {/* Analysis Section (Phase 4 - Placeholder) */}
          {recordingDetails.analysisStatus === 'completed' && (
            <>
              <Divider />
              <div>
                <Title level={5}>AI Analysis</Title>
                <Descriptions bordered size="small" column={2}>
                  {recordingDetails.relevanceScore !== undefined && (
                    <Descriptions.Item label="Relevance Score" span={2}>
                      <Progress 
                        percent={recordingDetails.relevanceScore} 
                        status={recordingDetails.relevanceScore > 70 ? 'success' : 'normal'}
                      />
                    </Descriptions.Item>
                  )}
                  {recordingDetails.goalMatch !== undefined && (
                    <Descriptions.Item label="Goal Match">
                      {recordingDetails.goalMatch ? (
                        <Tag color="green" icon={<FontAwesomeIcon icon={faCheckCircle} />}>Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )}
                    </Descriptions.Item>
                  )}
                  {recordingDetails.sentiment && (
                    <Descriptions.Item label="Sentiment">
                      <Tag color={
                        recordingDetails.sentiment === 'positive' ? 'green' :
                        recordingDetails.sentiment === 'negative' ? 'red' : 'default'
                      }>
                        {recordingDetails.sentiment}
                      </Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
                {recordingDetails.analysisNotes && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                    <Text strong>Summary:</Text>
                    <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>
                      {recordingDetails.analysisNotes}
                    </Paragraph>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Metadata */}
          <Divider />
          <Descriptions title="Metadata" size="small" column={2}>
            <Descriptions.Item label="Uploaded">
              {moment(recordingDetails.uploadedAt).format('MMM DD, YYYY HH:mm')}
            </Descriptions.Item>
            {recordingDetails.transcribedAt && (
              <Descriptions.Item label="Transcribed">
                {moment(recordingDetails.transcribedAt).format('MMM DD, YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      ) : (
        <Alert message="Recording not found" type="error" />
      )}
    </Modal>
  );
};

export default RecordingDetailsModal;
